export const ENTRY_MODE_MANUAL = "MANUAL" as const;
export const ENTRY_MODE_CALCULATED = "CALCULATED" as const;
export const ENTRY_MODES = [ENTRY_MODE_MANUAL, ENTRY_MODE_CALCULATED] as const;
export type EntryMode = (typeof ENTRY_MODES)[number];

export const ROLLUP_MODES = ["SUM", "AVG", "NONE"] as const;
export type RollupMode = (typeof ROLLUP_MODES)[number];

export const KPI_OPERATIONS = [
  "SUM",
  "SUBTRACT",
  "MULTIPLY",
  "DIVIDE",
  "AVERAGE",
  "MIN",
  "MAX",
] as const;
export type KpiOperation = (typeof KPI_OPERATIONS)[number];

export type KpiOperand = {
  source_kpi_id: number;
  source_activity_id: number;
  list_order: number;
  source_kpi_name?: string | null;
  source_activity_name?: string | null;
  kpi_operand_id?: number;
};

export type KpiDependsOn = {
  activity_id: number;
  activity_name: string;
  templates: Array<{
    report_template_id: number;
    report_template_name: string;
  }>;
};

export type KpiOption = {
  kpi_option_id?: number;
  label: string;
  list_order: number;
  allows_input?: boolean;
  is_active?: boolean;
};

export type KpiGridColumnOption = {
  kpi_grid_column_option_id?: number;
  label: string;
  list_order: number;
  allows_input?: boolean;
  is_active?: boolean;
};

export type KpiGridColumn = {
  kpi_grid_column_id?: number;
  column_key?: string | null;
  label: string;
  data_type_id: number;
  list_order: number;
  include_in_rollup?: boolean;
  is_active?: boolean;
  min_selections?: number | null;
  max_selections?: number | null;
  options?: KpiGridColumnOption[];
  /** Fill options from a predefined list instead of typed options (null = typed). */
  predefined_option_list_code?: string | null;
  /** Form responses only: why the list couldn't be filled on this form. */
  option_list_error?: string | null;
};

/** Admin form / API shape for fixed GRID rows. Do not send row_key on create/update. */
export type KpiGridFixedRow = {
  kpi_grid_fixed_row_id?: number;
  /** Server-owned; present on GET responses only. */
  row_key?: string | null;
  label: string;
  list_order: number;
  is_active?: boolean;
};

export function isSelectDataTypeName(name?: string | null): boolean {
  return (name || "").toUpperCase() === "SELECT";
}

export function isMultiselectDataTypeName(name?: string | null): boolean {
  return (name || "").toUpperCase() === "MULTISELECT";
}

export function isGridDataTypeName(name?: string | null): boolean {
  return (name || "").toUpperCase() === "GRID";
}

/** Keep in sync with DataTypeConstants::isStructuredType (ntcog-api). */
export function isStructuredDataTypeName(name?: string | null): boolean {
  return isSelectDataTypeName(name) || isMultiselectDataTypeName(name) || isGridDataTypeName(name);
}

/** First-column types allowed when the KPI has active fixed rows. */
export function isGridHeadingDataTypeName(name?: string | null): boolean {
  const n = (name || "").toUpperCase();
  return n === "VARCHAR" || n === "LONG_TEXT";
}

export function isPercentageDataTypeName(name?: string | null): boolean {
  return (name || "").toUpperCase() === "PERCENTAGE";
}

/** Keep in sync with DataTypeConstants::isMeasuredQuantityType (ntcog-api). */
export function isMeasuredQuantityDataTypeName(name?: string | null): boolean {
  const n = (name || "").toUpperCase();
  return [
    "INTEGER",
    "INTEGER_SIGNED",
    "INTEGER_UNSIGNED",
    "DECIMAL",
    "DECIMAL_SIGNED",
    "DECIMAL_UNSIGNED",
    "FLOAT",
    "CURRENCY",
  ].includes(n);
}

export function isNumericCompatibleDataTypeName(name?: string | null): boolean {
  const n = (name || "").toUpperCase();
  return [
    "INTEGER",
    "INTEGER_SIGNED",
    "INTEGER_UNSIGNED",
    "DECIMAL",
    "DECIMAL_SIGNED",
    "DECIMAL_UNSIGNED",
    "FLOAT",
    "PERCENTAGE",
    "CURRENCY",
    "BOOLEAN",
  ].some((token) => n === token || n.includes(token));
}

export function formatEntryModeLabel(mode?: string | null): string {
  if (!mode) return "Manual";
  return mode.toUpperCase() === ENTRY_MODE_CALCULATED ? "Calculated" : "Manual";
}

export function buildFormulaPreview(
  operation: string | null | undefined,
  operands: Array<{
    source_kpi_id: number;
    source_kpi_name?: string | null;
    source_activity_id?: number | null;
    source_activity_name?: string | null;
    activity_id?: number | null;
    activity_name?: string | null;
    kpi_name?: string | null;
  }>,
  kpiNameById?: Map<number, string>
): string {
  if (!operation || operands.length === 0) return "";
  const names = operands.map((op) => {
    const kpiName =
      op.source_kpi_name?.trim() ||
      op.kpi_name?.trim() ||
      kpiNameById?.get(op.source_kpi_id) ||
      `KPI #${op.source_kpi_id}`;
    const activityName =
      op.source_activity_name?.trim() || op.activity_name?.trim() || "";
    return activityName ? `${kpiName} (${activityName})` : kpiName;
  });
  const op = operation.toUpperCase();
  if (op === "SUBTRACT" || op === "DIVIDE") {
    return names.length >= 2
      ? `${names[0]} ${op === "SUBTRACT" ? "−" : "÷"} ${names[1]}`
      : `${op}(${names.join(", ")})`;
  }
  if (op === "MULTIPLY" && names.length === 2) {
    return `${names[0]} × ${names[1]}`;
  }
  return `${op}(${names.join(", ")})`;
}
